import { useState } from 'react';
import type { RefObject } from 'react';
import type { FmbImage } from '@/consts/frauMeetsBoerse';
import FmbImageSlot from '@/components/frauMeetsBoerse/FmbImageSlot';

interface FmbHeroVideoProps {
  thumbnail: FmbImage;
  embedUrl: string | null;
  className?: string;
  /** aria-label des Play-Buttons — Standard: 'Video abspielen' */
  ariaLabel?: string;
  /**
   * Abspielen von außen steuern. Bleibt der Wert undefined, verwaltet die
   * Komponente den Zustand wie bisher selbst.
   */
  isPlaying?: boolean;
  /** Wird beim Klick auf den Play-Button gerufen */
  onPlay?: () => void;
  /** Ref auf das Player-iframe, z. B. um player.js anzubinden */
  iframeRef?: RefObject<HTMLIFrameElement | null>;
  /** Kleinerer Play-Button für kompakte Karten (z. B. in der Testimonial-Leiste) */
  compact?: boolean;
}

/**
 * Video mit Click-to-Play: Zunächst wird nur das Thumbnail mit Play-Button
 * gerendert — der Bunny-Player (und damit der Request an
 * iframe.mediadelivery.net) lädt erst nach Klick. Dadurch bleibt die Seite
 * auch mit mehreren Videos schnell. Ohne embedUrl verhält sich die Komponente
 * wie ein reiner Bild-Slot (Platzhalter, solange die Links noch fehlen).
 */
export default function FmbHeroVideo({
  thumbnail,
  embedUrl,
  className,
  ariaLabel,
  isPlaying,
  onPlay,
  iframeRef,
  compact = false
}: FmbHeroVideoProps) {
  const [selfPlaying, setSelfPlaying] = useState<boolean>(false);
  const playing = isPlaying ?? selfPlaying;

  if (embedUrl === null) {
    return <FmbImageSlot image={thumbnail} className={className} />;
  }

  if (playing) {
    const autoplayUrl = `${embedUrl}${embedUrl.includes('?') ? '&' : '?'}autoplay=true`;
    return (
      <div className={`${className ?? ''} overflow-hidden bg-black`}>
        <iframe
          ref={iframeRef}
          src={autoplayUrl}
          title={thumbnail.alt}
          className="h-full w-full border-0"
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media; accelerometer; gyroscope"
          allowFullScreen
        />
      </div>
    );
  }

  const handlePlay = () => {
    setSelfPlaying(true);
    onPlay?.();
  };

  return (
    <button
      type="button"
      aria-label={ariaLabel ?? 'Video abspielen'}
      onClick={handlePlay}
      className={`${className ?? ''} group relative block cursor-pointer overflow-hidden`}
    >
      <FmbImageSlot image={thumbnail} className="h-full w-full" />
      <span className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/10">
        <span
          className={`flex items-center justify-center rounded-full bg-white/95 shadow-[0_10px_30px_var(--fmb-shadow-hero)] transition-transform group-hover:scale-110 ${
            compact ? 'h-[52px] w-[52px]' : 'h-[76px] w-[76px]'
          }`}
        >
          <span
            className={`h-0 w-0 border-y-transparent border-l-[var(--fmb-accent)] ${
              compact ? 'ml-1 border-y-[9px] border-l-[15px]' : 'ml-1.5 border-y-[14px] border-l-[22px]'
            }`}
          />
        </span>
      </span>
    </button>
  );
}
